/**
 * Unified Therapist Dashboard
 * Combines session control + emotion analysis into single draggable modal
 * Features: drag, opacity control, minimize, tabs
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import DraggableModal from './DraggableModal';
import SessionControlTab from './SessionControlTab';
import EmotionAnalysisTab from './EmotionAnalysisTab';
import { isTherapist, getSessionId } from '../functions';
import { useDashboardState } from '../hooks/useDashboardState';
import { useEmotionSocket } from '../hooks/useEmotionSocket';
const useStyles = makeStyles()((theme) => ({
    tabs: {
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
        paddingBottom: '8px'
    },
    tab: {
        flex: 1,
        padding: '12px 20px',
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px 8px 0 0',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'center',
        userSelect: 'none',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.08)',
            color: 'rgba(255, 255, 255, 0.8)',
            transform: 'translateY(-2px)'
        }
    },
    activeTab: {
        background: 'linear-gradient(135deg, rgba(11, 44, 74, 0.3) 0%, rgba(11, 108, 227, 0.2) 100%)',
        borderColor: 'rgba(11, 108, 227, 0.5)',
        color: '#FFFFFF',
        borderBottom: 'none',
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(11, 44, 74, 0.4) 0%, rgba(11, 108, 227, 0.3) 100%)',
            transform: 'none'
        }
    },
    minimizedSummary: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '8px 0',
        fontSize: '13px',
        color: 'rgba(255, 255, 255, 0.8)'
    },
    summaryItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    },
    summaryLabel: {
        color: 'rgba(255, 255, 255, 0.6)'
    },
    summaryValue: {
        fontWeight: 600,
        color: '#FFFFFF'
    },
    statusBadge: {
        padding: '4px 10px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase'
    },
    toggleButton: {
        position: 'fixed',
        top: '20px',
        right: '20px',
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #0b2c4a 0%, #0b6ce3 100%)',
        border: '2px solid rgba(11, 108, 227, 0.5)',
        color: '#FFFFFF',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        boxShadow: '0 4px 16px rgba(11, 44, 74, 0.4)',
        transition: 'all 0.2s ease',
        zIndex: 99998,
        '&:hover': {
            transform: 'scale(1.1)',
            boxShadow: '0 6px 24px rgba(11, 108, 227, 0.6)'
        },
        '&:active': {
            transform: 'scale(0.95)'
        }
    }
}));
export default function UnifiedTherapistDashboard() {
    const { classes, cx } = useStyles();
    // Get Redux state
    const state = useSelector((state) => state);
    // Check if user is therapist (do this early but don't return yet - hooks must be called first!)
    const isUserTherapist = isTherapist(state);
    // ===========================================
    // ALL HOOKS MUST BE CALLED BEFORE ANY RETURNS
    // (React hooks rules - hooks must be called unconditionally)
    // ===========================================
    // Dashboard state
    const { isOpen, isMinimized, opacity, activeTab, toggleOpen, toggleMinimize, setOpacity, setActiveTab } = useDashboardState();
    // Session state
    const [sessionState, setSessionState] = useState('waiting');
    const [sessionStartTime, setSessionStartTime] = useState(null);
    const [lobbyParticipants, setLobbyParticipants] = useState([]);
    // Emotion detection - use stable participant ID (useState ensures it doesn't change between renders)
    const sessionId = getSessionId();
    const [ownParticipantId] = useState(() => `therapist-${Date.now()}`);
    const { connected, participants, error } = useEmotionSocket(isUserTherapist ? sessionId : null, // Only connect if therapist
    ownParticipantId);
    // NOW we can do conditional returns (after all hooks are called)
    if (!isUserTherapist) {
        return null;
    }
    // Dashboard is rendering
    // Session control handlers
    const handleStartSession = () => {
        setSessionState('active');
        setSessionStartTime(new Date());
    };
    const handleEndSession = () => {
        setSessionState('ended');
    };
    const handleAdmitParticipant = (participantId) => {
        // @ts-ignore - Jitsi global API
        if (typeof APP !== 'undefined' && APP.conference) {
            // @ts-ignore
            APP.conference.lobbyApproveAccess(participantId);
        }
        setLobbyParticipants(prev => prev.filter(p => p.id !== participantId));
    };
    const handleRejectParticipant = (participantId) => {
        // @ts-ignore - Jitsi global API
        if (typeof APP !== 'undefined' && APP.conference) {
            // @ts-ignore
            APP.conference.lobbyDenyAccess(participantId);
        }
        setLobbyParticipants(prev => prev.filter(p => p.id !== participantId));
    };
    // Minimized content summary
    const minimizedContent = (React.createElement("div", { className: classes.minimizedSummary },
        React.createElement("div", { className: classes.summaryItem },
            React.createElement("span", { className: classes.summaryLabel }, "Patients:"),
            React.createElement("span", { className: classes.summaryValue }, participants.size)),
        React.createElement("div", { className: classes.summaryItem },
            React.createElement("span", { className: classes.summaryLabel }, "Status:"),
            React.createElement("span", { className: cx(classes.statusBadge), style: {
                    background: sessionState === 'active' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 193, 7, 0.2)',
                    color: sessionState === 'active' ? '#4CAF50' : '#FFC107'
                } }, sessionState)),
        connected && (React.createElement("div", { className: classes.summaryItem },
            React.createElement("span", { style: { color: '#4CAF50' } }, "\u25CF"),
            React.createElement("span", { className: classes.summaryValue }, "Live")))));
    // If dashboard is closed, show toggle button only
    if (!isOpen) {
        return ReactDOM.createPortal(React.createElement("button", { style: {
                position: 'fixed',
                top: '20px',
                right: '20px',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0b2c4a 0%, #0b6ce3 100%)',
                border: '3px solid #0b6ce3',
                color: '#FFFFFF',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                boxShadow: '0 4px 20px rgba(11, 108, 227, 0.6)',
                zIndex: 999999
            }, onClick: toggleOpen, "data-testid": "open-dashboard-btn", title: "Open Therapist Dashboard" }, "\uD83E\uDE7A"), document.body);
    }
    return ReactDOM.createPortal(React.createElement(DraggableModal, { title: "Therapist Control", isMinimized: isMinimized, opacity: opacity, onMinimizeToggle: toggleMinimize, onClose: toggleOpen, onOpacityChange: setOpacity, minimizedContent: minimizedContent, testId: "unified-therapist-dashboard" },
        React.createElement("div", { className: classes.tabs },
            React.createElement("button", { className: cx(classes.tab, activeTab === 'session' && classes.activeTab), onClick: () => setActiveTab('session'), "data-testid": "session-control-tab-btn" }, "\uD83D\uDCCB Session Control"),
            React.createElement("button", { className: cx(classes.tab, activeTab === 'emotion' && classes.activeTab), onClick: () => setActiveTab('emotion'), "data-testid": "emotion-analysis-tab-btn" }, "\uD83E\uDDE0 Emotion Analysis")),
        activeTab === 'session' ? (React.createElement(SessionControlTab, { sessionState: sessionState, sessionStartTime: sessionStartTime, lobbyParticipants: lobbyParticipants, onStartSession: handleStartSession, onEndSession: handleEndSession, onAdmitParticipant: handleAdmitParticipant, onRejectParticipant: handleRejectParticipant })) : (React.createElement(EmotionAnalysisTab, { connected: connected, participants: participants, error: error }))), document.body);
}
